<template>
  <div v-if="list != null">
    <!-- Above List -->
    <div
      class="relative flex justify-center items-center mb-1 pt-5 overflow-hidden"
    >
      <p
        class="leading-none mb-1 absolute text-contrast-dark text-chplus-regular"
        style="left:50%;top:0;transform:translateX(-50%)"
      >
        {{ getListMatch(list.candidates.map(entry => entry.id)).toFixed(0)
        }}<span class="text-chplus-tooltip">%</span>
      </p>
      <div v-if="showLaurel" class="relative smart-spider-box">
        <icon-laurel
          class="laurel absolute text-interaction-dark"
          style="transform:translateX(-50%);left:50%;top:-1rem"
        />
        <smart-spider
          class="pb-5"
          :show-background="true"
          :whiteBorder="true"
          :showUser="false"
          :listId="listId"
        />
      </div>

      <div v-else class="relative smart-spider-box">
        <smart-spider
          :show-background="true"
          :faded-lines="true"
          :showUser="false"
          :listId="listId"
        />

        <button
          v-show="list.id === choice"
          class="absolute button-choice"
          :class="{ 'button-choice-visible': showChoice }"
          style="top: 50%;right:-0.5rem;"
          @click="showChooseConfirmation"
        >
          <icon-heart-filled v-if="list.id === choice" />
          <icon-heart-empty v-else />
        </button>
      </div>

      <list-tools
        v-if="showTools"
        class="absolute right-0 text-interaction-dark"
        style="bottom: 0.2rem"
        :list="list"
        :allowed-actions="allowedActions"
        :quick-actions="quickActions"
        :showEditNotification="!hasFavoriteList"
        @choose="showChooseConfirmation"
        @copy="showCopyConfirmation = true"
        @edit="
          $router.push({ name: 'list-editor', params: { listId: list.id } })
        "
        @delete="showDeleteConfirmation = true"
        @showelectionview="
          $router.push({ name: 'list-view', params: { listId: list.id } })
        "
        @expand="onToolsToggled"
      />
    </div>
    <!-- List -->
    <div class="border-2 border-gray-500 rounded-lg overflow-hidden relative">
      <list-header :list="list" class="mb-3" @header-clicked="toggleExpanded">
        <template v-if="allowCollapse" v-slot:action-right>
          <!-- Collapse Button -->
          <button @click="toggleExpanded">
            <icon-angle :direction="toggle ? 'up' : 'down'" class="text-2xl" />
          </button>
        </template>
      </list-header>
      <!-- Candidates -->
      <div class="p-4" v-if="toggle">
        <div
          class="text-chplus-s text-red-600 font-bold border-b-2 lg:border-b-2px border-gray-200 pb-4 col-span-full"
          v-if="electionView"
        >
          {{ $t("components.list.list.popup_list_summary_info") }}
        </div>
        <candidate-slot-grid
          class="w-full"
          :candidate-ids="candidateIds"
          :candidate-size="'small'"
          :list-id="listId"
          @mountedCandidateSlotGrid="
            $emit('mountedCandidateSlotGrid', { listId })
          "
          v-if="!electionView && candidateIds.length > 0"
        ></candidate-slot-grid>
        <span
          v-else-if="!electionView && list.adapted === null"
          class="block mx-auto text-center text-chplus-s text-contrast-light mb-4"
          >{{ $t("components.list.official.pending") }}</span
        >
        <template v-else-if="electionView">
          <div
            class="w-full max-w-xs"
            v-for="entry in list.candidates"
            :key="entry.pos"
          >
            <router-link
              :to="{
                name: 'candidate',
                params: { candidateId: entry.id, electionId: activeElection.id }
              }"
            >
              <candidate-list-item
                :candidate-id="entry.id"
                :active-list="list"
              />
            </router-link>
          </div>
        </template>
      </div>
    </div>

    <alert :show="showCopyConfirmation">
      <template v-slot:button> </template>
      <template v-slot:title>
        {{ $t("components.list.list.question_to_copy") }}
      </template>
      <template v-slot:footer>
        <div class="flex w-full">
          <navigate-small-button
            :theme="'border-interaction-dark'"
            class="flex-1 uppercase"
            @click="showCopyConfirmation = false"
            style="margin-right: 3%"
          >
            {{ $t("components.list.list.button_no_copy") }}
          </navigate-small-button>
          <navigate-small-button
            :theme="'contrast-light'"
            @click="copyList"
            class="flex-1"
          >
            {{ $t("components.list.list.button_yes_copy") }}
          </navigate-small-button>
        </div>
      </template>
    </alert>

    <alert :show="showDeleteConfirmation">
      <template v-slot:button> </template>
      <template v-slot:title>
        {{ $t("components.list.list.question_to_delete") }}
      </template>
      <template v-slot:footer>
        <div class="flex w-full">
          <navigate-small-button
            :theme="'border-interaction-dark'"
            class="flex-1 uppercase"
            @click="showDeleteConfirmation = false"
            style="margin-right: 3%"
          >
            {{ $t("components.list.list.button_no_delete") }}
          </navigate-small-button>
          <navigate-small-button
            :theme="'contrast-light'"
            @click="deleteList"
            class="flex-1"
          >
            {{ $t("components.list.list.button_yes_delete") }}
          </navigate-small-button>
        </div>
      </template>
    </alert>
  </div>
</template>

<script>
import { mapGetters } from "vuex";

import SmartSpider from "@/components/chart/SmartSpider";
import IconLaurel from "@/components/icons/IconLaurel";

import { ListMatch } from "@/utils/ListMatch";
import IconAngle from "@/components/icons/IconAngle";
import Vue from "vue";
import ListHeader from "./ListHeader";
import CandidateSlotGrid from "@/components/people/CandidateSlotGrid";
import CandidateListItem from "@/components/people/CandidateListItem";
import ListTools from "@/components/list/ListTools";
import Alert from "@/components/layouts/Alert";
import IconHeartFilled from "@/components/icons/IconHeartFilled";
import IconHeartEmpty from "@/components/icons/IconHeartEmpty";
import { ProgressStepId } from "@/utils/ProgressStepId";
import NavigateSmallButton from "@/components/buttons/NavigateSmallButton";

export default {
  name: "List",
  components: {
    NavigateSmallButton,
    IconHeartEmpty,
    IconHeartFilled,
    Alert,
    ListTools,
    CandidateSlotGrid,
    CandidateListItem,
    ListHeader,
    IconAngle,
    SmartSpider,
    IconLaurel
  },
  mixins: [ListMatch],
  props: {
    listId: {
      type: Number,
      required: true
    },
    /**
     * Whether to show the candidates with additional details for the election.
     * When true, shows the details, when false shows candidates as slots.
     */
    electionView: {
      type: Boolean,
      default: false
    },
    expanded: {
      type: Boolean,
      default: false
    },
    expandedBus: {
      type: Vue,
      default: null
    },
    allowCollapse: {
      type: Boolean,
      default: true
    },
    showLaurel: {
      type: Boolean,
      default: false
    },
    showElectionStatus: {
      type: Boolean,
      default: false
    },
    showTools: {
      type: Boolean,
      default: true
    },
    chooseConfirmationBus: {
      type: Vue,
      default: null
    }
  },
  data: () => ({
    toggle: false,
    showCopyConfirmation: false,
    showDeleteConfirmation: false,
    showChoice: true,
    hasFavoriteList: false,
    allowedActions: [],
    quickActions: []
  }),
  beforeCreate() {
    this.toggle = false;
  },
  created() {
    this.checkProgression();
  },
  mounted() {
    this.toggle = this.electionView || this.expanded;
    this.updateListTools();
  },
  computed: {
    ...mapGetters("list", ["choice"]),
    ...mapGetters("user", ["user", "hasProgress"]),
    ...mapGetters("election", ["activeElection"]),
    list() {
      return this.$store.getters["list/list"](this.listId);
    },
    candidateIds() {
      const result = [];
      const candidates = this.list.candidates;
      result.length = candidates.length;
      for (let entry of candidates.filter(e => e.pos > 0)) {
        result[entry.pos - 1] = entry.id;
      }
      return result;
    },
    isListEmpty() {
      return this.candidateIds.length <= 0;
    }
  },
  watch: {
    list() {
      this.updateListTools();
    }
  },
  methods: {
    updateListTools() {
      const list = this.list;
      const isOfficial = list.adapted === null;
      this.allowedActions = [];
      this.quickActions = [];
      this.allowedActions.push("copy");
      this.allowedActions.push("choose");
      if (isOfficial) {
        this.quickActions.push("copy");
        this.allowedActions.push("showelectionview");
      } else {
        this.allowedActions.push("edit");
        this.allowedActions.push("delete");
        if (this.choice === list.id) {
          this.allowedActions.push("showelectionview");
        }
        this.quickActions.push("edit");
      }
    },
    async copyList() {
      let listId = await this.$store.dispatch("list/copyList", {
        listId: this.list.id
      });
      await this.$router.push({ name: "list-editor", params: { listId } });
    },
    async deleteList() {
      await this.$store.dispatch("list/deleteList", { listId: this.list.id });
    },
    async chooseList() {
      if (this.choice !== this.list.id) {
        await this.$store.dispatch("list/chooseList", { listId: this.list.id });
      } else {
        await this.$store.dispatch("list/deleteChoice");
      }
    },
    checkProgression() {
      this.hasFavoriteList = this.hasProgress(ProgressStepId.FavoriteList);
    },
    toggleExpanded() {
      this.toggle = !this.toggle;
      let expandedBus = this.expandedBus;
      if (expandedBus) {
        expandedBus.$emit("toggle", {
          list: this.listId,
          expanded: this.toggle
        });
      }
    },
    showChooseConfirmation() {
      if (this.chooseConfirmationBus)
        this.chooseConfirmationBus.$emit("choose", {
          listId: this.listId,
          callback: this.chooseList
        });
      else this.chooseList();
    },
    onToolsToggled(expanded) {
      this.showChoice = !expanded;
    }
  }
};
</script>

<style scoped>
.smart-spider-box {
  width: 4.09375rem;
  height: 4.09375rem;
}
.laurel {
  width: 7.09375rem;
  height: 7.09375rem;
}
.button-choice {
  width: 1.65625rem;
  height: 1.65625rem;
  transition: transform 0.1s ease-in-out;
}
.button-choice:not(.button-choice-visible) {
  transform: translate(100%, -50%) scale(0);
}
.button-choice.button-choice-visible {
  transform: translate(100%, -50%) scale(1);
  transition-delay: 0.1s;
}
/deep/ .button-choice .svg-icon {
  font-size: 1.65625rem;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
@media screen and (min-width: 768px) {
  .button-choice {
    width: 1.875rem;
    height: 1.875rem;
  }
  /deep/ .button-choice .svg-icon {
    font-size: 1.875rem;
  }
  .smart-spider-box {
    width: 4.84375rem;
    height: 4.84375rem;
  }
  .laurel {
    width: 7.84375rem;
    height: 7.84375rem;
  }
}

.list-group {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(20em, 1fr));
  grid-auto-rows: auto;
  gap: 0.5em;
}
</style>
